import {RecipientCandidateSort, RecipientSelectionDTO, RequestRecipientMode} from "@/api/data-contracts";

/**
 * Pure selection-state logic for the "Empfänger der Anfrage festlegen" dialog. No React, no DOM, no
 * Axios calls — the Vitest-testable half of the dialog (see RecipientSelectionDialog.tsx for the
 * rendering half).
 *
 * The selection is tracked as a set of company ids, not row objects, so it survives the candidate list
 * being re-fetched on search and sort changes: the rows are discarded and reloaded, the id set is not.
 * "N von M ausgewählt" reads N as the size of this set; M is the `totalElements` of the first,
 * unfiltered fetch and is not owned by this module (it never changes with the search).
 */

/** The two mutually exclusive top-level choices of the dialog. */
export type RecipientSelectionMode = "ALL" | "SELECTED";

/**
 * Toggles a single company in the selection: adds it when absent, removes it when present. Returns a
 * new `Set`, the input set is never mutated.
 */
export function toggleRecipient(selected: ReadonlySet<number>, companyId: number): Set<number> {
    const next = new Set(selected);
    if (next.has(companyId)) {
        next.delete(companyId);
    } else {
        next.add(companyId);
    }
    return next;
}

/**
 * Returns true when every currently displayed row is already selected. Drives both the bulk-action
 * button label ("Alle anhaken" vs. "Alle abwählen") and the direction {@link applyBulkSelection} takes.
 * An empty display list (no search hits) counts as "not all selected", so the button stays in its
 * select state rather than offering to deselect nothing.
 */
export function areAllDisplayedSelected(
    selected: ReadonlySet<number>,
    displayedCompanyIds: readonly number[]
): boolean {
    if (displayedCompanyIds.length === 0) {
        return false;
    }
    return displayedCompanyIds.every((id) => selected.has(id));
}

/**
 * Applies the bulk action to the currently displayed rows only: selects every displayed row when not
 * all of them are selected yet, otherwise deselects every displayed row. A selection outside the
 * currently displayed rows (hidden by an active search) is never touched either way.
 */
export function applyBulkSelection(
    selected: ReadonlySet<number>,
    displayedCompanyIds: readonly number[]
): Set<number> {
    const next = new Set(selected);
    if (areAllDisplayedSelected(selected, displayedCompanyIds)) {
        displayedCompanyIds.forEach((id) => next.delete(id));
    } else {
        displayedCompanyIds.forEach((id) => next.add(id));
    }
    return next;
}

/**
 * Counts selected companies that are not among the currently displayed rows, i.e. hidden by an active
 * search. Drives the hidden-selection hint below the list.
 */
export function countHiddenSelected(selected: ReadonlySet<number>, displayedCompanyIds: readonly number[]): number {
    const displayed = new Set(displayedCompanyIds);
    return [...selected].filter((id) => !displayed.has(id)).length;
}

/**
 * Determines whether "Veröffentlichen" may be confirmed: always true for mode "ALL" (the default,
 * targeting every whitelisted company as before), true for mode "SELECTED" only once at least one
 * company is checked.
 */
export function isPublishEnabled(mode: RecipientSelectionMode, selected: ReadonlySet<number>): boolean {
    return mode === "ALL" || selected.size > 0;
}

/**
 * Translates the dialog's local selection state into the payload the publish call sends to the server.
 * Mode "ALL" always carries an empty company list, even when the user had checked companies before
 * switching back to "ALL": the server rejects mode ALL with a non-empty `companyIds` (Phase 1.3). Mode
 * "SELECTED" carries the checked ids in the `Set`'s insertion order, which is stable across repeated
 * builds of the same selection.
 */
export function buildRecipientSelection(
    mode: RecipientSelectionMode,
    selected: ReadonlySet<number>
): RecipientSelectionDTO {
    if (mode === "ALL") {
        return { mode: RequestRecipientMode.ALL, companyIds: [] };
    }
    return { mode: RequestRecipientMode.SELECTED, companyIds: [...selected] };
}

// Canonical MUI sort-header rule (mirrors the library's own TableSortLabel usage example): a column
// that is not yet active always starts ascending on its first click, only an already-active column
// flips direction. Computing the target from the raw direction alone (ignoring which column is active)
// would send an inactive column straight to descending on its first click, which contradicts the
// ascending-first expectation the dialog documents for "Entfernung".

/**
 * Determines the next {@link RecipientCandidateSort} after a click on a column header. Clicking the
 * already-active column in ascending order flips it to descending; every other case (a different
 * column, or the active column already descending) lands on that column's ascending value.
 */
export function nextRecipientSort(
    current: RecipientCandidateSort,
    column: "NAME" | "DISTANCE"
): RecipientCandidateSort {
    if (column === "NAME") {
        return current === RecipientCandidateSort.NAME_ASC
            ? RecipientCandidateSort.NAME_DESC
            : RecipientCandidateSort.NAME_ASC;
    }
    return current === RecipientCandidateSort.DISTANCE_ASC
        ? RecipientCandidateSort.DISTANCE_DESC
        : RecipientCandidateSort.DISTANCE_ASC;
}
